// 3rd party libs
import React, { ReactElement, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

// Components
import { Button, WarningPrompt } from 'components';

import { GroupItem } from 'types/user-management';
import { Id } from 'types';
import {
  useAddEditGroupUserManagementTwoMutation,
  useDeleteGroupUserManagementV2Mutation,
  useRemoveAlertNotificationPermissionsMutation
} from 'api';
import { isValidGroup } from 'pages/UserManagement/components/Group/AddEditGroup/Scope/ScopeUtils';
import { ToastMsg } from 'common/components/Toast';
import theme from 'themes';
import { IcoTrash } from 'icons/indexed-icons/IcoTrash';
import { RightUpDiagArrowIcon } from 'icons/indexed-icons/RightUpDiagArrowIcon';
import { Link } from 'react-router-dom';
import { BtnContainer, Footer } from '../../User/UserModal';
import CustomLoader from 'components/CustomLoader';

const DeleteContextWrapper = styled.div`
  margin-bottom: 0.75rem;
  padding: 0 1rem;
  border-bottom: 0.063rem solid ${theme.colors.lightGrey4};
  margin-top: 1.5rem;
`;
const DeleteHeader = styled.div`
  color: #000;
  font-family: Roboto;
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-bottom: 0.75rem;
`;
const DeletePrompt = styled.div`
  color: ${theme.colors.text.lightBlack}
  font-family: Roboto;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
const DeleteButton = styled.div`
  display: flex;
  justify-content: right;
  margin-top: 0.75rem;
`;
const MoveToGroupContainer = styled.div`
  display: flex;
  gap: 0.35rem;
  margin-left: 1rem;
  margin-right: 1rem;
  margin-bottom: 10rem;
  cursor: pointer;
`;
interface Props {
  tableItemId?: Id;
  onClose: () => void;
  onCloseVerifyData: () => void;
  groupInfo?: GroupItem;
  isChanged?: boolean;
}

const GroupModalFooter = ({
  tableItemId,
  onClose,
  onCloseVerifyData,
  groupInfo,
  isChanged
}: Props): ReactElement => {
  const saveGroupRef = useRef<boolean>(false);
  const [isViewConfirmDelete, setIsViewConfirmDelete] = useState(false);
  const [deletedGroup, { isLoading: isLoadingDeleteGroup }] =
    useDeleteGroupUserManagementV2Mutation();
  const [saveGroup, { isLoading: isLoadingSaveGroup }] = useAddEditGroupUserManagementTwoMutation();
  const [removeAlertNotificationPermissions] = useRemoveAlertNotificationPermissionsMutation();
  const [alertPermissionsUpdated, setalertPermissionsUpdated] = useState(false);
  const alertPermissionsUpdatedRef = useRef(alertPermissionsUpdated);

  useEffect(() => {
    alertPermissionsUpdatedRef.current = alertPermissionsUpdated;
  }, [alertPermissionsUpdated]);

  useEffect(() => {
    // Track if read permission is removed for Alert Management
    const alertManagementPermission = groupInfo?.permissions?.find(
      (permission) => permission.permissionId === '3939a1bb-b029-4258-a5eb-605f1b4f7a5a'
    );

    if (alertManagementPermission && alertManagementPermission.read === false) {
      setalertPermissionsUpdated(true);
    } else {
      setalertPermissionsUpdated(false);
    }
  }, [groupInfo?.permissions]);

  const onGroupDelete = useCallback(async () => {
    if (tableItemId) {
      deletedGroup({ groupId: tableItemId })
        .unwrap()
        .then(() => {
          ToastMsg({
            message: `Group deleted successfully.`,
            heading: 'Group',
            type: 'success',
            position: 'top-center'
          });
          onClose();
        })
        .catch((err) => {
          console.error('Failed to delete', err);
          const { data } = err;
          ToastMsg({
            message: data?.detail ?? 'There was a problem deleting the group.',
            heading: 'Group',
            type: 'info',
            position: 'top-center'
          });
        });
      setIsViewConfirmDelete(false);
    }
  }, [tableItemId, onClose]);

  const onGroupSave = useCallback(async () => {
    if (isEmpty(groupInfo) || (!isEmpty(groupInfo) && !isValidGroup(groupInfo))) {
      ToastMsg({
        message: `Organizations, Lines and machines can not be empty.`,
        heading: 'Group',
        type: 'warning',
        position: 'top-center'
      });
    } else if (groupInfo) {
      const allUsers = [
        ...(groupInfo?.users ? groupInfo.users : []),
        ...(groupInfo?.existingUsers ? groupInfo.existingUsers : [])
      ];
      if (!saveGroupRef.current) {
        saveGroupRef.current = true;
        // Save the group
        saveGroup({ ...groupInfo, users: allUsers })
          .unwrap()
          .then(() => {
            ToastMsg({
              message: `The group has been ${tableItemId ? 'updated.' : 'created.'}`,
              heading: 'Group',
              type: 'success',
              position: 'top-center'
            });
            if (alertPermissionsUpdatedRef.current) {
              try {
                removeAlertNotificationPermissions({
                  user_ids: (groupInfo?.users ?? [])
                    .map((user) => user.id)
                    .filter((id): id is string => id !== undefined),
                  group_ids: groupInfo?.id ? [groupInfo.id] : []
                })
                  .unwrap()
                  .catch((error) => {
                    console.error('Failed to remove alert notification permissions', error);
                  });
              } catch (error) {
                console.error('Failed to remove alert notification permissions', error);
              }
            }
            onClose();
          })
          .catch((err) => {
            console.error('Failed to save', err);
            const { data, status } = err;
            if (status === 500) {
              return ToastMsg({
                message: 'There was a problem saving the group.',
                heading: 'Group',
                type: 'info',
                position: 'top-center'
              });
            }
            ToastMsg({
              message: data?.detail ?? 'There was a problem saving the group.',
              heading: 'Group',
              type: 'info',
              position: 'top-center'
            });
          });
      }
    }
  }, [groupInfo, tableItemId, onClose, saveGroupRef]);

  const allPermissionsFalse = groupInfo?.permissions?.every(
    (permission) => !permission.read && !permission.write
  );

  // Name, permission and at least 1 scope are required.
  const isDisabledSave = useMemo(
    () =>
      !!groupInfo?.validationError ||
      isLoadingDeleteGroup ||
      isLoadingSaveGroup ||
      isEmpty(groupInfo) ||
      isEmpty(groupInfo.name) ||
      isEmpty(groupInfo.permissions) ||
      allPermissionsFalse ||
      (!isEmpty(groupInfo) && !isValidGroup(groupInfo)) ||
      isLoadingSaveGroup ||
      (isEmpty(groupInfo.organizations) && !groupInfo.allOrganizations),
    [groupInfo, isLoadingSaveGroup]
  );
  const isDisabledBtn = useMemo(
    () =>
      isLoadingSaveGroup || isLoadingDeleteGroup || (groupInfo?.users && groupInfo.users.length > 0)
        ? true
        : false,
    [groupInfo]
  );

  return (
    <>
      <WarningPrompt
        helperText={`Are you sure you want to delete this group? This action cannot be undone!`}
        isConfirmPrompt
        isVisible={isViewConfirmDelete}
        onCancelCallback={() => setIsViewConfirmDelete(false)}
        onConfirmCallback={() => onGroupDelete()}
        title={`Remove "${groupInfo?.name}"`}
      />
      {tableItemId && (
        <DeleteContextWrapper>
          <DeleteHeader>Delete Group</DeleteHeader>
          <DeletePrompt>
            By deleting the group, you will lose all the data and the member access will also be
            removed. The group will need to be empty in order to delete the group.
          </DeletePrompt>
          <DeleteButton>
            <Button
              disabled={isDisabledBtn}
              variant="text"
              onClick={() => setIsViewConfirmDelete(true)}
              icon={
                <IcoTrash width={'2rem'} color={isDisabledBtn ? theme.colors.negativeRed3 : ''} />
              }
              color={theme.colors.text.error}
            >
              Delete Group
            </Button>
          </DeleteButton>
        </DeleteContextWrapper>
      )}
      {tableItemId && (
        <MoveToGroupContainer>
          <Link to={`/user-management-v2/groups/${tableItemId}`}>Move to group page</Link>
          <RightUpDiagArrowIcon />
        </MoveToGroupContainer>
      )}
      <Footer className="add-group-footer">
        <BtnContainer>
          <Button
            variant="link"
            borderRadius="3rem"
            borderColor={theme.colors.primaryBlue5}
            width="6.688rem"
            height="2rem"
            onClick={onCloseVerifyData}
          >
            Cancel
          </Button>
          <Button
            disabled={isDisabledSave || !isChanged}
            onClick={onGroupSave}
            variant="hover-blue"
            borderRadius="3rem"
            bgColor={theme.colors.primaryBlue5}
            color={theme.colors.lightGrey3}
            width="6.688rem"
            height="2rem"
          >
            {tableItemId ? 'Update' : 'Save'}
            { isLoadingSaveGroup && 
              <CustomLoader padding='0 0 0 .5rem' thickness='.3rem' size='1.2rem' />
            }
          </Button>
        </BtnContainer>
      </Footer>
    </>
  );
};

export default GroupModalFooter;
